import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { countPdfPages } from "@/lib/pdf-thumbnail/readPdf";
import { cn } from "@/lib/utils";
import { REGISTRATION_PAGE } from "@/settings/constant";
import { Icon as SDSIcon, Spinner, Typography } from "@bookips/sds";
import Image from "next/image";
import { forwardRef, useRef, useState } from "react";
import { useDialogStore } from "src/store/useDialogStore";
import useSnackbar from "src/store/useSnackbar";
import Icon from "../icon/Icon";
import { Sizes } from "../icon/IconList";
const FileUploadBox = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { icon , title , description , fileType , isLoading , onFileSelect , file , maxFileSize =fileType === "image" ? 30 : 100 , error , errorMessage , onFileDelete , onPdfPageCountChange , showPreview =fileType === "image" , ...props } = param;
    const { isLoggedIn  } = useAuthStore();
    const { open , close  } = useDialogStore();
    const { redirectToLogin , redirectTo  } = useRedirect();
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pdfPageCount, setPdfPageCount] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const { addSnackbar  } = useSnackbar();
    const getAcceptedFileTypes = ()=>{
        switch(fileType){
            case "image":
                return [
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "application/pdf"
                ];
            case "pdf":
                return [
                    "application/pdf"
                ];
            default:
                return [];
        }
    };
    const getFileTypeErrorMessage = ()=>{
        switch(fileType){
            case "image":
                return "PNG, JPG, JPEG, PDF 파일만 등록 가능해요.";
            case "pdf":
                return "PDF 파일만 등록 가능해요.";
            default:
                return "지원하지 않는 파일 형식이에요.";
        }
    };
    const getDeleteConfirmMessage = ()=>{
        switch(fileType){
            case "image":
                return {
                    title: "기본 디자인으로 변경할까요?",
                    description: "기본 디자인으로 변경하면 등록된 이미지 파일은 삭제돼요."
                };
            case "pdf":
                return {
                    title: "정말로 삭제할까요?",
                    description: "삭제된 파일은 다시 복구할 수 없어요."
                };
            default:
                return {
                    title: "파일을 삭제할까요?",
                    description: "삭제된 파일은 다시 복구할 수 없어요."
                };
        }
    };
    const handleLabelClick = (e)=>{
        var _fileInputRef_current;
        e.preventDefault();
        if (file) return;
        if (!isLoggedIn) {
            open({
                id: "login-required",
                type: "confirm",
                props: {
                    title: "로그인이 필요해요.",
                    description: "로그인 또는 회원가입을 진행해주세요.",
                    confirmText: "회원가입",
                    cancelText: "로그인",
                    onConfirm: ()=>{
                        redirectTo(REGISTRATION_PAGE);
                    },
                    onCancel: ()=>{
                        redirectToLogin();
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            close("login-required");
                        }
                    }
                }
            });
            return;
        }
        (_fileInputRef_current = fileInputRef.current) === null || _fileInputRef_current === void 0 ? void 0 : _fileInputRef_current.click();
    };
    const handleFileChange = async (e)=>{
        var _e_target_files;
        const file = (_e_target_files = e.target.files) === null || _e_target_files === void 0 ? void 0 : _e_target_files[0];
        const acceptedTypes = getAcceptedFileTypes();
        if (file && !acceptedTypes.includes(file.type)) {
            open({
                id: "file-validity-error",
                type: "confirm",
                props: {
                    severity: "error",
                    title: getFileTypeErrorMessage(),
                    description: "파일의 확장자가 맞는지 확인 후 다시 등록해주세요.",
                    onConfirm: ()=>{
                        e.target.value = "";
                    }
                }
            });
            return;
        }
        if (file && file.size > maxFileSize * 1024 * 1024) {
            open({
                id: "max-file-size-error",
                type: "confirm",
                props: {
                    title: "".concat(maxFileSize, "MB까지 등록 가능해요."),
                    description: "파일 용량을 확인 후 다시 등록해주세요.",
                    severity: "error",
                    onConfirm: ()=>{
                        e.target.value = "";
                    }
                }
            });
            e.target.value = "";
            return;
        }
        if (file && onFileSelect) {
            setIsProcessing(true);
            try {
                if (fileType === "pdf") {
                    await getPdfPageCount(file);
                } else if (fileType === "image" && file.type !== "application/pdf" && showPreview) {
                    const url = URL.createObjectURL(file);
                    setPreviewUrl(url);
                }
                onFileSelect(file);
            } catch (error) {
                ;
            } finally{
                setIsProcessing(false);
            }
        }
        e.target.value = "";
    };
    const getPdfPageCount = async (file)=>{
        try {
            const arrayBuffer = await file.arrayBuffer();
            const total = await countPdfPages(arrayBuffer);
            setPdfPageCount(total);
            onPdfPageCountChange === null || onPdfPageCountChange === void 0 ? void 0 : onPdfPageCountChange(total);
        } catch (error) {
            setPdfPageCount(null);
            open({
                id: "pdf-upload-error",
                type: "confirm",
                props: {
                    severity: "error",
                    title: "파일 등록에 실패했어요.",
                    description: "손상된 파일이거나 지원하지 않는 형식이에요.\n파일을 확인 후 다시 등록해주세요.",
                    onConfirm: ()=>{
                        onFileSelect === null || onFileSelect === void 0 ? void 0 : onFileSelect(null);
                        setPdfPageCount(null);
                    }
                }
            });
        }
    };
    const handleDeleteFile = (e)=>{
        e.stopPropagation();
        const { title , description  } = getDeleteConfirmMessage();
        open({
            id: "delete-file",
            type: "confirm",
            props: {
                title,
                description,
                severity: "error",
                confirmText: fileType === "image" ? "변경하기" : "삭제",
                onConfirm: ()=>{
                    onFileSelect === null || onFileSelect === void 0 ? void 0 : onFileSelect(null);
                    onFileDelete === null || onFileDelete === void 0 ? void 0 : onFileDelete();
                    setPreviewUrl(null);
                    setPdfPageCount(null);
                    if (fileType === "pdf") {
                        addSnackbar({
                            message: "파일을 삭제했어요.",
                            variant: "error"
                        });
                    }
                },
                confirmButtonColor: fileType === "image" ? "primary" : "danger",
                cancelText: "취소"
            }
        });
    };
    const isCurrentlyLoading = isLoading || isProcessing;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("label", {
                htmlFor: "".concat(fileType, "-file-input"),
                ref: ref,
                className: cn("p-5 border border-gray-200 rounded-md border-dashed flex flex-col items-center justify-center hover:bg-gray-300/20 w-full", file ? "hover:bg-gray-0 bg-gray-0 border-none" : "cursor-pointer", error && "border-status-error"),
                onClick: handleLabelClick,
                children: isCurrentlyLoading ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spinner, {
                            size: "lg",
                            className: "text-primary-main"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "subtitle1",
                            className: "text-primary mt-3",
                            children: "파일 등록 중…"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-secondary mt-1",
                            as: "span",
                            children: "파일 등록은 최대 5분 이상 소요될 수 있어요."
                        })
                    ]
                }) : file ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between w-full",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center",
                            children: [
                                /*#__PURE__*/ _jsx(Icon, {
                                    icon: icon,
                                    size: Sizes.large
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col items-start ml-5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "subtitle1",
                                            className: "text-primary text-left line-clamp-1",
                                            children: file.name
                                        }),
                                        fileType === "pdf" && pdfPageCount !== null && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "body2",
                                            className: "text-secondary mt-1",
                                            as: "span",
                                            children: "PDF 페이지 수: ".concat(pdfPageCount, "p")
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            className: "p-3",
                            onClick: handleDeleteFile,
                            children: /*#__PURE__*/ _jsx(SDSIcon.Close_Fill, {
                                className: "size-6 text-status-error"
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            icon: icon,
                            size: Sizes.large
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "subtitle1",
                            className: "text-primary mt-3",
                            children: title
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "body2",
                            className: "text-secondary mt-1 whitespace-pre-line text-center",
                            as: "span",
                            children: description
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("input", {
                id: "".concat(fileType, "-file-input"),
                ref: fileInputRef,
                type: "file",
                accept: getAcceptedFileTypes().join(","),
                className: "sr-only",
                onChange: handleFileChange,
                ...props
            }),
            error && /*#__PURE__*/ _jsx(Typography, {
                variant: "body2",
                className: "text-status-error ml-2 mt-1",
                children: errorMessage
            }),
            file && previewUrl && showPreview && fileType === "image" && file.type !== "application/pdf" && /*#__PURE__*/ _jsx("div", {
                className: "bg-gray-0 rounded-md flex items-center justify-center mt-2 py-[38px]",
                children: /*#__PURE__*/ _jsx(Image, {
                    src: previewUrl,
                    alt: "커스텀 이미지",
                    width: 200,
                    height: 200,
                    className: "w-[250px] rounded-md"
                })
            })
        ]
    });
});
FileUploadBox.displayName = "FileUploadBox";
export default FileUploadBox;
